import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Volume from '@/images/Volume.svg';
import VolumeCross from '@/images/VolumeCross.svg';
import { StyledSlider } from '@/pages/AdvancedEditor/components/panels/common/StyledSlider';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import theme from '@/theme';
import styled from '@emotion/styled';
import Tooltip from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';
import { ControlNumberInput } from '../../../common/ControlNumberInput';

const Container = styled.div`
    display: flex;
    gap: 16px;
    justify-content: center;
    align-items: center;
    width: 220px;
`;

const StyledIconButton = styled(IconButton)`
    color: ${({ theme }) => theme.colors.secondary.main} !important;
    background-color: #fff !important;
    font-weight: 600;
    border-radius: 8px;
    display: inline-flex;
    font-size: 16px;
    width: 32px;
    height: 32px;
    border: 1px solid ${theme.colors.black[300]};

    p {
        margin-left: 8px;
    }

    :focus {
        outline: none;
    }

    :hover {
        outline: 1px solid ${({ theme }) => theme.colors.black[200]};
        background-color: ${({ theme }) => theme.colors.black[200]} !important;
    }
`;

export const VolumeControl = observer(() => {
    const { store } = useAdvancedEditorContext();
    const elements = store.selectedShapes;
    const element = elements[0] as ElementType & { volume: number };

    const handleChange = (value: number) => {
        const volume = parseFloat((value / 100).toFixed(2));

        elements.forEach(element => element.set({ volume }));
    };

    const handleMute = () => {
        if (element.volume === 0) {
            handleChange(1);
        } else {
            handleChange(0);
        }
    };

    const volume = Math.round(element.volume * 100);

    return (
        <Container>
            <Tooltip
                title="Mute"
                placement="top"
                slotProps={{ tooltip: { sx: { backgroundColor: theme.colors.secondary.main, padding: '8px', fontSize: '14px' } } }}
                PopperProps={{
                    modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
                }}>
                <div>
                    <StyledIconButton ariaLabel="Mute" onClick={handleMute}>
                        {element.volume ? (
                            <MaskedIcon src={Volume} size="18px" color={theme.colors.secondary.main} />
                        ) : (
                            <MaskedIcon src={VolumeCross} size="18px" color={theme.colors.secondary.main} />
                        )}
                    </StyledIconButton>
                </div>
            </Tooltip>

            <StyledSlider value={volume} onChange={(_e, value) => handleChange(value as number)} step={1} min={0} max={100} />

            <ControlNumberInput value={volume} onChange={handleChange} min={0} max={100} suffix="%" />
        </Container>
    );
});
